// src/components/AllForms/AllForms.tsx
import React, {FC} from 'react'
import {Input} from '../../Input/Input'
import {i18n} from '../../../translate/i18n'

// -----------------------------------------------------------------------------
// Tipos utilitários
// -----------------------------------------------------------------------------
type ChangeHandler<T = any> = (value: T) => void
export type Option = {value: string | number; label: string}

// Helpers para o Input "select" (ele espera {value, select})
const toSelectOpts = (opts: Option[]) => opts.map((o) => ({value: o.value, select: o.label}))

// Mocks simples para os previews
const SAMPLE_ITEMS: Option[] = [
  {value: 'svc1', label: i18n.t('service_requests.sample_service_1')},
  {value: 'svc2', label: i18n.t('service_requests.sample_service_2')},
]
const SAMPLE_CATEGORIES: Option[] = [
  {value: 'higiene', label: i18n.t('service_requests.sample_hygiene')},
  {value: 'bebidas', label: i18n.t('service_requests.sample_drinks')},
]

// -----------------------------------------------------------------------------
// 1) Select de números do quarto
// -----------------------------------------------------------------------------
type RoomNumberSelectBoxProps = {
  value: number | undefined
  onChange: ChangeHandler<number>
  min?: number
  max?: number
  label?: string
  disabled?: boolean
  placeholder?: string
  idInput?: string
}
export const RoomNumberSelectBox: FC<RoomNumberSelectBoxProps> = ({
  value,
  onChange,
  min = 1,
  max = 10,
  label = i18n.t('service_requests.room_number_label'),
  disabled,
  placeholder = i18n.t('service_requests.select_placeholder'),
  idInput,
}) => {
  const options = Array.from({length: max - min + 1}, (_, i) => {
    const n = min + i
    return {value: n, select: String(n)}
  })

  return (
    <Input
      inputAttr={{type: 'select'}}
      label={label}
      value={value ?? ''}
      change={(v: string) => onChange(Number(v))}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
      idInput={idInput}
    />
  )
}

// -----------------------------------------------------------------------------
// 2) Campo de data
// -----------------------------------------------------------------------------
type DateBoxProps = {
  value: string
  onChange: ChangeHandler<string>
  label?: string
  min?: string // 'YYYY-MM-DD'
  max?: string // 'YYYY-MM-DD'
  disabled?: boolean
  idInput?: string
}
export const DateBox: FC<DateBoxProps> = ({
  value,
  onChange,
  label = i18n.t('service_requests.date_label'),
  min,
  max,
  disabled,
  idInput,
}) => (
  <Input
    inputAttr={{type: 'date'}}
    label={label}
    value={value}
    change={(v: string) => onChange(v)}
    minLength={min}
    maxLength={max}
    disabled={disabled}
    idInput={idInput}
  />
)

// -----------------------------------------------------------------------------
// 3) Campo de hora (HH:mm)
// -----------------------------------------------------------------------------

type HourBoxProps = {
  value?: string
  onChange: ChangeHandler<string>
  label?: string
  disabled?: boolean
  idInput?: string
  placeholder?: string
}
export const HourBox: React.FC<HourBoxProps> = ({
  value,
  onChange,
  label = i18n.t('service_requests.hour_label'),
  disabled,
  idInput,
  placeholder = i18n.t('service_requests.select_placeholder'),
}) => {
  const options = Array.from({length: 24}, (_, h) => {
    const hh = String(h).padStart(2, '0')
    const v = `${hh}:00`
    return {value: v, select: v}
  })

  return (
    <Input
      inputAttr={{type: 'select'}}
      label={label}
      value={value ?? ''}
      change={(v: string) => onChange(v)}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
      idInput={idInput}
    />
  )
}

// -----------------------------------------------------------------------------
// 3.1) Data + Hora lado a lado
// -----------------------------------------------------------------------------
type DateHourBoxProps = {
  date: string
  hour: string
  onChangeDate: ChangeHandler<string>
  onChangeHour: ChangeHandler<string>
  labels?: {date?: string; hour?: string}
  disabled?: boolean
}
export const DateHourBox: FC<DateHourBoxProps> = ({
  date,
  hour,
  onChangeDate,
  onChangeHour,
  labels,
  disabled,
}) => (
  <div className='d-flex'>
    <div className='flex-grow-1'>
      <DateBox
        value={date}
        onChange={onChangeDate}
        label={labels?.date ?? i18n.t('service_requests.date_label')}
        disabled={disabled}
      />
    </div>
    <div style={{minWidth: 160}}>
      <HourBox
        value={hour}
        onChange={onChangeHour}
        label={labels?.hour ?? i18n.t('service_requests.hour_label')}
        disabled={disabled}
      />
    </div>
  </div>
)

// -----------------------------------------------------------------------------
// 4) Numérico simples (se quiser manter um bloco separado de quantidade)
// -----------------------------------------------------------------------------
type QuantityInputBoxProps = {
  value: number | undefined
  onChange: ChangeHandler<number | ''>
  label?: string
  min?: number
  max?: number
  disabled?: boolean
  textCenter?: boolean
  idInput?: string
}
export const QuantityInputBox: FC<QuantityInputBoxProps> = ({
  value,
  onChange,
  label = i18n.t('service_requests.quantity_label'),
  min = 1,
  max = 999,
  disabled,
  textCenter = true,
  idInput,
}) => (
  <Input
    inputAttr={{type: 'number'}}
    label={label}
    value={value ?? ''}
    change={(v: string) => {
      if (v === '') return onChange('')
      const n = Number(v)
      onChange(Number.isNaN(n) ? '' : n)
    }}
    minNumber={min}
    maxNumber={max}
    disabled={disabled}
    textCenter={textCenter}
    idInput={idInput}
    placeholder={`${i18n.t('service_requests.between_placeholder')} ${min} ${i18n.t(
      'service_requests.and_placeholder'
    )} ${max}`}
  />
)

// -----------------------------------------------------------------------------
// 5) Seleções — componentes separados
// -----------------------------------------------------------------------------

// 5.1) Select-list: apenas um select simples listando itens
type SelectListBoxProps = {
  value?: string | number
  onChange?: ChangeHandler<string | number>
  items?: Option[]
  label?: string
  placeholder?: string
  disabled?: boolean
  idInput?: string
}
export const SelectListBox: FC<SelectListBoxProps> = ({
  value,
  onChange,
  items = SAMPLE_ITEMS,
  label = i18n.t('service_requests.select_list_label'),
  placeholder = i18n.t('service_requests.select_item_placeholder'),
  disabled,
  idInput,
}) => (
  <Input
    inputAttr={{type: 'select'}}
    label={label}
    value={value ?? ''}
    change={(v: any) => onChange?.(v)}
    options={toSelectOpts(items)}
    placeholder={placeholder}
    disabled={disabled}
    idInput={idInput}
  />
)

// 5.2) Select-checkbox: select de categoria + checkboxes dos itens
type SelectCheckboxBoxProps = {
  category?: string | number
  onChangeCategory?: ChangeHandler<string | number>
  categories?: Option[]
  items?: Option[]
  label?: string
  disabled?: boolean
  idInput?: string
}
export const SelectCheckboxBox: FC<SelectCheckboxBoxProps> = ({
  category,
  onChangeCategory,
  categories = SAMPLE_CATEGORIES,
  items = SAMPLE_ITEMS,
  label = i18n.t('service_requests.select_checkbox_label'),
  disabled,
  idInput,
}) => (
  <div className='d-grid gap-3'>
    <Input
      inputAttr={{type: 'select'}}
      label={label}
      value={category ?? ''}
      change={(v: any) => onChangeCategory?.(v)}
      options={toSelectOpts(categories)}
      placeholder={i18n.t('service_requests.select_category_placeholder')}
      disabled={disabled}
      idInput={idInput}
    />
    <div className='d-grid gap-2'>
      {items.map((it) => (
        <label key={String(it.value)} className='form-check'>
          <input className='form-check-input' type='checkbox' disabled />
          <span className='form-check-label'>{it.label}</span>
        </label>
      ))}
    </div>
  </div>
)

// 5.3) Select-list: + input de quantidade
type SelectCountBoxProps = {
  category?: string | number
  onChangeCategory?: ChangeHandler<string | number>
  categories?: Option[]
  items?: Option[]
  label?: string
  disabled?: boolean
  idInput?: string
}
export const SelectCountBox: FC<SelectCountBoxProps> = ({
  category,
  onChangeCategory,
  categories = SAMPLE_CATEGORIES,
  items = SAMPLE_ITEMS,
  label = i18n.t('service_requests.select_quantity_label'),
  disabled,
  idInput,
}) => (
  <div className='d-grid gap-3'>
    <Input
      inputAttr={{type: 'select'}}
      label={label}
      value={category ?? ''}
      change={(v: any) => onChangeCategory?.(v)}
      options={toSelectOpts(categories)}
      placeholder={i18n.t('service_requests.select_category_placeholder')}
      disabled={disabled}
      idInput={idInput}
    />
    <div className='d-grid gap-2'>
      {items.map((it) => (
        <div key={String(it.value)} className='d-flex align-items-center gap-3'>
          <div className='flex-grow-1'>{it.label}</div>
          <div style={{width: 140}}>
            <QuantityInputBox value={undefined} onChange={() => {}} label='' />
          </div>
        </div>
      ))}
    </div>
  </div>
)

// 5.4) Select-count: select de categoria + lista de itens com input de quantidade

// 5.4) Select-count: select de item + input de quantidade
type SelectListQuantityProps = {
  // SELECT
  value?: string | number
  onChange?: ChangeHandler<string | number>
  items?: Option[]
  label?: string
  placeholder?: string
  disabled?: boolean
  idInput?: string

  // QUANTIDADE
  valueQty?: number | ''
  onChangeQty?: ChangeHandler<number | ''>
  labelQty?: string
  min?: number
  max?: number
  disabledQty?: boolean
  textCenterQty?: boolean
  idInputQty?: string
  qtyWidthPx?: number
}

export const SelectListQuantity: FC<SelectListQuantityProps> = ({
  // SELECT
  value,
  onChange,
  items = SAMPLE_ITEMS,
  label = i18n.t('service_requests.select_list_label'),
  placeholder = i18n.t('service_requests.select_item_placeholder'),
  disabled,
  idInput,

  // QUANTIDADE
  valueQty = '',
  onChangeQty,
  labelQty = i18n.t('service_requests.qty_label'),
  min = 1,
  max = 999,
  disabledQty,
  textCenterQty = true,
  idInputQty,
  qtyWidthPx = 140,
}) => (
  <div className='d-flex align-items-end gap-3'>
    {/* Select ocupa o espaço */}
    <div className='flex-grow-1' style={{minWidth: 0}}>
      <Input
        inputAttr={{type: 'select'}}
        label={label}
        value={value ?? ''}
        change={(v: any) => onChange?.(v)}
        options={toSelectOpts(items)}
        placeholder={placeholder}
        disabled={disabled}
        idInput={idInput}
      />
    </div>

    {/* Campo de quantidade com largura fixa */}
    <div className='flex-shrink-0' style={{width: qtyWidthPx, minWidth: qtyWidthPx}}>
      <Input
        inputAttr={{type: 'number'}}
        label={labelQty}
        value={valueQty === '' ? '' : valueQty}
        change={(v: string) => {
          if (!onChangeQty) return
          if (v === '') return onChangeQty('')
          const n = Number(v)
          onChangeQty(Number.isNaN(n) ? '' : n)
        }}
        minNumber={min}
        maxNumber={max}
        disabled={disabledQty}
        textCenter={textCenterQty}
        idInput={idInputQty}
        placeholder={`${i18n.t('service_requests.between_placeholder')} ${min} ${i18n.t(
          'service_requests.and_placeholder'
        )} ${max}`}
      />
    </div>
  </div>
)

// -----------------------------------------------------------------------------
// PREVIEW LOCK + CATÁLOGO
// -----------------------------------------------------------------------------
export const PreviewLock: React.FC<React.PropsWithChildren> = ({children}) => (
  <div style={{pointerEvents: 'none', userSelect: 'none'}} aria-hidden tabIndex={-1}>
    {children}
  </div>
)

export type FormBlockKind =
  | 'numberRoom'
  | 'date'
  | 'hour'
  | 'dateHour'
  | 'selectList'
  | 'selectCheckBox'
  | 'selectCount'
  | 'quantity'
  | 'selectListQuantity'

export type FormBlockMeta = {
  id: FormBlockKind
  title: string
  icon?: string // ícone Bootstrap Icons
  getPreview: () => JSX.Element
}

// catálogo para o menu lateral (cada bloco com preview independente)
export const FORMS_CATALOG: FormBlockMeta[] = [
  {
    id: 'numberRoom',
    title: i18n.t('service_requests.catalog_room_number'),
    icon: 'bi-door-closed',
    getPreview: () => (
      <PreviewLock>
        <RoomNumberSelectBox value={1} onChange={() => {}} />
      </PreviewLock>
    ),
  },
  {
    id: 'date',
    title: i18n.t('service_requests.catalog_date'),
    icon: 'bi-calendar',
    getPreview: () => (
      <PreviewLock>
        <DateBox value={''} onChange={() => {}} />
      </PreviewLock>
    ),
  },
  {
    id: 'hour',
    title: i18n.t('service_requests.catalog_hour'),
    icon: 'bi-clock',
    getPreview: () => (
      <PreviewLock>
        <HourBox value={''} onChange={() => {}} />
      </PreviewLock>
    ),
  },
  {
    id: 'dateHour',
    title: i18n.t('service_requests.catalog_date_hour'),
    icon: 'bi-calendar3-week',
    getPreview: () => (
      <PreviewLock>
        <DateHourBox date={''} hour={''} onChangeDate={() => {}} onChangeHour={() => {}} />
      </PreviewLock>
    ),
  },

  // === NOVOS BLOCOS DE SELEÇÃO INDIVIDUAIS ===
  {
    id: 'selectList',
    title: i18n.t('service_requests.catalog_select_list'),
    icon: 'bi-list-ul',
    getPreview: () => (
      <PreviewLock>
        <SelectListBox />
      </PreviewLock>
    ),
  },
  {
    id: 'selectListQuantity',
    title: i18n.t('service_requests.catalog_select_list_quantity'),
    icon: 'bi-list-check',
    getPreview: () => (
      <PreviewLock>
        <SelectListQuantity
          value={undefined}
          onChange={() => {}}
          valueQty={1}
          onChangeQty={() => {}}
        />
      </PreviewLock>
    ),
  },
  {
    id: 'selectCheckBox',
    title: i18n.t('service_requests.catalog_select_checkbox'),
    icon: 'bi-list-check',
    getPreview: () => (
      <PreviewLock>
        <SelectCheckboxBox />
      </PreviewLock>
    ),
  },
  {
    id: 'selectCount',
    title: i18n.t('service_requests.catalog_select_count'),
    icon: 'bi-123',
    getPreview: () => (
      <PreviewLock>
        <SelectCountBox />
      </PreviewLock>
    ),
  },

  // opcional: campo numérico autônomo
  {
    id: 'quantity',
    title: i18n.t('service_requests.catalog_quantity'),
    icon: 'bi-123',
    getPreview: () => (
      <PreviewLock>
        <QuantityInputBox value={1} onChange={() => {}} />
      </PreviewLock>
    ),
  },
]
